import { SUPPORTED_LANGUAGES } from '../../../employees/utils/employee-languages';
import { combineWords } from '../../../util/formatters';
import { isTruthy } from '../../../util/is-truthy';
import { LANGUAGES, LanguageCode } from '../../../util/languages';

const conditionallyPluralizeRecipient = (count: number) =>
  `${count > 1 ? `${count} recipients` : 'recipient'}`;

const conjugatePrefer = (count: number) => `prefer${count > 1 ? '' : 's'}`;

const getMonolingualUnsupportedLanguageBannerMessage = (
  language: string,
  languageCounts: Record<LanguageCode, number>,
) => {
  const recipientCount = languageCounts[language];

  return `This message will be sent to the ${conditionallyPluralizeRecipient(
    recipientCount,
  )} who ${conjugatePrefer(recipientCount)} to communicate in ${LANGUAGES[language]}.`;
};

const getDefaultLanguageBannerMessage = (
  language: string,
  languageCounts: Record<LanguageCode, number>,
  messagesByLanguage: Record<LanguageCode, string>,
) => {
  const recipientLanguages: Array<string> = [];
  const otherSupportedLanguagesWithMessage: Array<string> = [];
  Object.keys(languageCounts).forEach((lang) => {
    if (SUPPORTED_LANGUAGES.includes(lang)) {
      if (lang !== language && lang in messagesByLanguage) {
        otherSupportedLanguagesWithMessage.push(lang);
      } else {
        recipientLanguages.push(lang);
      }
    } else {
      recipientLanguages.push(lang);
    }
  });

  const recipientCount = recipientLanguages.reduce(
    (sum, recipientLanguage) => sum + languageCounts[recipientLanguage],
    0,
  );
  const otherSupportedLanguagesRecipientCount = otherSupportedLanguagesWithMessage.reduce(
    (sum, otherSupportedLanguage) => sum + languageCounts[otherSupportedLanguage],
    0,
  );
  const recipientLanguagesNames = recipientLanguages.map(
    (recipientLanguage) => LANGUAGES[recipientLanguage],
  );

  if (otherSupportedLanguagesWithMessage.length === 0) {
    return `This message will be sent to all${
      recipientCount > 1 ? ` ${recipientCount}` : ''
    } recipients, regardless of language preference.`;
  }

  const sentences = [
    `This message will be sent to the ${conditionallyPluralizeRecipient(
      recipientCount,
    )} who ${conjugatePrefer(recipientCount)} to communicate in ${combineWords(
      recipientLanguagesNames,
    )}.`,
  ];

  if (otherSupportedLanguagesWithMessage.length) {
    const otherRecipientWording = conditionallyPluralizeRecipient(
      otherSupportedLanguagesRecipientCount,
    );

    sentences.push(
      `The ${otherRecipientWording} ${
        otherSupportedLanguagesWithMessage.length > 1
          ? ''
          : `who ${conjugatePrefer(otherSupportedLanguagesRecipientCount)} ${
              LANGUAGES[otherSupportedLanguagesWithMessage[0]]
            }`
      } will receive the version${otherSupportedLanguagesWithMessage.length > 1 ? 's' : ''} below.`,
    );
  }

  return sentences.join(' ');
};

const getNonDefaultLanguageBannerMessage = (
  language: string,
  languageCounts: Record<string, number>,
  messagesByLanguage: Record<string, string>,
) => {
  const recipientCount = languageCounts[language];
  const languageName = LANGUAGES[language];
  const prefix = isTruthy(messagesByLanguage[language])
    ? 'This message will be sent to the'
    : 'Enter a message to be sent to the';
  return `${prefix} ${conditionallyPluralizeRecipient(recipientCount)} who ${conjugatePrefer(
    recipientCount,
  )} to communicate in ${languageName}.`;
};

export const legacyGetLanguagePreferenceBannerMessages = (
  languageCounts: Record<LanguageCode, number>,
  messagesByLanguage: Record<LanguageCode, string>,
  defaultLanguage = 'en',
) => {
  const recipientLanguages = Object.keys(languageCounts);
  if (recipientLanguages.length === 1 && !SUPPORTED_LANGUAGES.includes(recipientLanguages[0])) {
    return {
      [defaultLanguage]: getMonolingualUnsupportedLanguageBannerMessage(
        recipientLanguages[0],
        languageCounts,
      ),
    };
  }

  const languagesWithMessage = Object.keys(messagesByLanguage);
  const supportedLanguages = languagesWithMessage.filter((language) =>
    SUPPORTED_LANGUAGES.includes(language),
  );

  return supportedLanguages.reduce((acc: Record<LanguageCode, string>, language: LanguageCode) => {
    const isDefaultLanguage = language === defaultLanguage;

    if (isDefaultLanguage) {
      acc[language] = getDefaultLanguageBannerMessage(language, languageCounts, messagesByLanguage);
    } else {
      acc[language] = getNonDefaultLanguageBannerMessage(
        language,
        languageCounts,
        messagesByLanguage,
      );
    }

    return acc;
  }, {});
};
